import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import { primeServices } from "@services/apis/Prime";
import {
  GetCadInstanceData,
  GetCadInstancesRequest,
  GetCadInstancesResponse,
} from "@services/models/Prime/GetCadInstancesModel";
import { RowClickedEvent } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import webSocketService from "@services/websocket/WebsocketServices";
import { createCanadaOrderColumnDefs } from "../../Prime/components/CanadaOrderComponents";
import ConfirmOpsClearModal from "../../Prime/components/ConfirmOpsClearModal";
import PrimeDetailsCanadaTabs from "../../Prime/PrimeDetailsCanadaTabs";
import { getPrimeOrderAgRowClass } from "../../Prime/utils/primeOrderRowClass";

const MobilePrimeOrderFlowCanada = ({
  onShowDetail,
}: {
  onShowDetail?: (instanceId: string) => void;
}) => {
  const gridRef = useRef<any>(null);
  const ROWS_PER_PAGE = 1000;

  const [orders, setOrders] = useState<GetCadInstanceData[]>([]);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [instanceId, setInstanceId] = useState<string | undefined>(undefined);
  const [tradeType, setTradeType] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const [showDetailView, setShowDetailView] = useState<boolean>(false);
  const [opsClearInstanceId, setOpsClearInstanceId] = useState<string | null>(
    null,
  );

  const columnDefs = useMemo(
    () => createCanadaOrderColumnDefs(setOpsClearInstanceId),
    [],
  );

  const opsClearOrder = useMemo(
    () =>
      orders.find((order) => order.instanceId === opsClearInstanceId) ?? null,
    [orders, opsClearInstanceId],
  );

  const handleEventChange = (id: string, newEventId: number) => {
    setOrders((prev) =>
      prev.map((order) =>
        order.instanceId === id ? { ...order, eventId: newEventId } : order,
      ),
    );
  };

  const didInitialFetch = useRef(false);
  const inFlight = useRef(false);

  const fetchCanadaOrders = async (pageNum: number, reset: boolean = false) => {
    if (inFlight.current) return;
    inFlight.current = true;

    const data: GetCadInstancesRequest = {
      type: tradeType || undefined,
      page: pageNum,
      rows: ROWS_PER_PAGE,
      searchTerm: searchTerm || undefined,
    };

    try {
      setIsLoading(true);
      const response: GetCadInstancesResponse =
        await primeServices.getCadInstancesList(data);
      if (response.data.length === 0) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
      if (reset) {
        setOrders(response.data);
      } else {
        setOrders((prevOrders) => [...prevOrders, ...response.data]);
      }
    } catch (error: any) {
      setError(error.message || "Failed to fetch Canada prime orders.");
    } finally {
      setIsLoading(false);
      inFlight.current = false;
    }
  };

  useEffect(() => {
    if (!didInitialFetch.current) {
      didInitialFetch.current = true;
      setPage(1);
      setHasMore(true);
      fetchCanadaOrders(1, true);
    }
  }, []);

  useEffect(() => {
    if (!didInitialFetch.current) return;
    setPage(1);
    setHasMore(true);
    fetchCanadaOrders(1, true);
  }, [tradeType]);

  useEffect(() => {
    if (hasMore && !searchTerm) {
      fetchCanadaOrders(page, false);
    }
  }, [page, hasMore, searchTerm]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (gridRef.current?.api) {
        gridRef.current.api.refreshCells({
          columns: ["active"],
          force: true,
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const jwt = secureLocalStorage.getItem("jwt");
    webSocketService.connect({ token: jwt }, true);
  }, []);

  const handleCanadaTradeChangeWebSockets = (
    socketData: GetCadInstanceData,
  ) => {
    if (searchTerm) {
      return;
    }

    if (tradeType && tradeType !== (socketData.isBuying ? "Payin" : "Payout")) {
      return;
    }

    setOrders((prevOrders) => {
      const existingInstanceIndex = prevOrders.findIndex(
        (order) => order.instanceId === socketData.instanceId,
      );

      if (existingInstanceIndex !== -1) {
        const updatedOrders = [...prevOrders];
        updatedOrders[existingInstanceIndex] = socketData;
        return updatedOrders;
      } else {
        if (prevOrders.length > 0) {
          const socketCreatedAt = new Date(socketData.createdAt).getTime();
          const newestCreatedAt = new Date(prevOrders[0].createdAt).getTime();

          if (socketCreatedAt > newestCreatedAt) {
            return [socketData, ...prevOrders];
          } else {
            return prevOrders;
          }
        }

        return [socketData, ...prevOrders];
      }
    });
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        webSocketService.protectedSocket?.off("cad_instance_list");

        if (!searchTerm) {
          webSocketService.onMessage(
            "cad_instance_list",
            true,
            handleCanadaTradeChangeWebSockets,
          );
        }
      } catch (error) {
        console.error("WebSocket initialization error:", error);
      }
    };
    initializeWebSocket();
  }, [webSocketService.protectedSocket?.connected, searchTerm, tradeType]);

  const onBodyScroll = useCallback(
    (params: any) => {
      if (!hasMore || isLoading || searchTerm) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, isLoading, searchTerm],
  );

  const onRowClicked = (event: RowClickedEvent) => {
    const selectedData = event.data;

    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const rowElement = document.querySelector(
        `[row-index="${event.rowIndex}"]`,
      );
      if (rowElement) {
        rowElement.classList.add("ag-row-selected-custom");
      }
    }

    setInstanceId(selectedData?.instanceId);
    if (onShowDetail) {
      onShowDetail(selectedData?.instanceId);
    } else {
      setShowDetailView(true);
    }
  };

  const handleSearchSubmit = async () => {
    try {
      setIsLoading(true);
      const data: GetCadInstancesRequest = {
        type: tradeType || undefined,
        page: 1,
        rows: ROWS_PER_PAGE,
        searchTerm: searchTerm || undefined,
      };
      const response: GetCadInstancesResponse =
        await primeServices.getCadInstancesList(data);
      setOrders(response.data);
      setHasMore(false);
    } catch (error: any) {
      setError(error.message || "Failed to fetch Canada prime orders.");
    } finally {
      setIsLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setPage(1);
    setHasMore(true);
  };

  if (showDetailView && instanceId && !onShowDetail) {
    return (
      <div className="flex flex-col h-full">
        <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-row items-center px-4 shrink-0 gap-3">
          <button onClick={() => setShowDetailView(false)} className="flex items-center justify-center cursor-pointer bg-transparent border-0 p-0">
            <span className="material-symbols-outlined text-[22px] text-text-base">arrow_back</span>
          </button>
          <p className="text-text-base text-[14px] font-semibold">X RAMP Trade Details</p>
        </div>
        <div className="flex-1 overflow-auto p-3">
          <PrimeDetailsCanadaTabs
            instanceId={instanceId}
            onEventChange={handleEventChange}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[12px]">
      <div className="flex flex-col gap-2 pt-[12px]">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-text-base text-[10px] font-bold whitespace-nowrap">
            Filter by:
          </p>
          {["Payin", "Payout"].map((type) => (
            <div
              key={type}
              onClick={
                isLoading
                  ? undefined
                  : () =>
                      setTradeType(tradeType === type ? undefined : type)
              }
              className={`border py-[4px] px-[12px] text-text-base text-[10px] font-medium rounded-[100px] ${
                isLoading
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              } ${
                tradeType === type
                  ? "border-border-button-secondary bg-bg-button-secondary"
                  : "border-border-subtle bg-bg-page-body"
              }`}
            >
              {type}
            </div>
          ))}
        </div>
        <div className="w-full">
          <GlobalSearchInputField
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            onSearch={handleSearchSubmit}
            onClear={clearSearch}
            disableSearchButton={!searchTerm.trim()}
            placeholder="Search Instance ID, Merchant Ref, Interac Ref, Email"
          />
        </div>
      </div>
      <div className="flex flex-col overflow-x-auto" style={{ height: "calc(100vh - 260px)" }}>
        {isLoading && page == 1 ? (
          <AgGridSkeleton
            columnDefs={columnDefs}
            style={{ height: "100%", minWidth: "1200px", width: "100%" }}
          />
        ) : (
          <div className="flex flex-col h-full" style={{ minWidth: "1200px" }}>
            <AgGridReactTable
              ref={gridRef}
              rowData={orders}
              columnDefs={columnDefs}
              onRowClicked={onRowClicked}
              onBodyScroll={onBodyScroll}
              getRowId={(params) => params.data.instanceId?.toString()}
              getRowClass={(params) =>
                getPrimeOrderAgRowClass("canada", params.data)
              }
              error={error}
            />
            {isLoading && orders.length > 0 && (
              <div className="flex items-center justify-center py-4 border-t border-border-subtle">
                <div className="flex items-center space-x-2 text-text-subtle">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary"></div>
                  <span className="text-sm">Loading more orders...</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <ConfirmOpsClearModal
        open={!!opsClearInstanceId}
        instanceId={opsClearInstanceId || undefined}
        market="canada"
        senderName={opsClearOrder?.senderName}
        senderEmail={opsClearOrder?.senderEmail}
        userDetails={opsClearOrder?.userDetails}
        userCountryImage={opsClearOrder?.userCountryImage}
        onClose={() => setOpsClearInstanceId(null)}
        onSuccess={(clearedId) => {
          setOrders((prev) =>
            prev.map((order) =>
              order.instanceId === clearedId
                ? { ...order, opsCleared: 1 }
                : order,
            ),
          );
        }}
      />
    </div>
  );
};

export default MobilePrimeOrderFlowCanada;
